import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SiteModel } from '../models/site.model';
import { AcsCamera } from '../models/acs.camera.model';
import { environment } from '../../environments/environment';
import { firstValueFrom, Observable } from 'rxjs';

/**
 * This service manages the Sites in the camera application database
 */
@Injectable({
  providedIn: 'root',
})
export class SiteService {
  sites: SiteModel[];

  constructor(private http: HttpClient) { }

  /**
   * Get all sites with optional user inclusion
   * @param  {boolean} includeUsers - Include Users
   * @param  {boolean} checkStatus - Check site online status (fire-and-forget)
   * @returns {Promise<SiteModel[]>} Promise
   */
  async getAllSites(
    includeUsers: boolean = false,
    checkStatus: boolean = false
  ): Promise<SiteModel[]> {
    return new Promise((resolve, reject) => {
      this.http
        .get<SiteModel[]>(
          `${environment.managementApi}/Sites/All?includeCameras=true&includeUsers=${includeUsers}`
        )
        .subscribe({
          next: (sites) => {
            if (checkStatus) {
              void this.refreshSitesStatus(sites);
            }
            resolve(sites);
          },
          error: (err) => reject(err)
        }
        );
    });
  }

  getSiteCameraStatus(siteId: number): Observable<AcsCamera[]> {
    return this.http.get<AcsCamera[]>(`${environment.imageApi}/image/site/${siteId}/cameras`);
  }

  /**
   * Check the connection status of multiple sites and update their status properties accordingly
   * @param  {SiteModel[]} sites - List of sites to check and update
   * @returns {Promise<void>} Promise
   */
  async refreshSitesStatus(sites: SiteModel[]): Promise<void> {
    if (!sites || sites.length === 0) {
      return;
    }

    // Limit the number of concurrent checks to avoid overwhelming the server
    const maxConcurrentChecks = 5;
    let nextIndex = 0;

    // Worker function to process sites in batches
    const worker = async (): Promise<void> => {
      while (true) {
        const index = nextIndex++;
        if (index >= sites.length) {
          return;
        }

        const site = sites[index];

        try {
          const cameras = await firstValueFrom(this.getSiteCameraStatus(site.Id));

          // If we successfully get a response, we can assume the site is online
          site.HasError = false;
          site.ErrorMessage = '';

          if (!site.IsAcs) {
            // If the site is not an ACS site, we won't have camera status to check
            continue;
          }

          // If no cameras are returned, log a warning and skip status updates
          if (!cameras) {
            console.warn(`No camera status returned for site [${site.Name}]`);
            continue;
          }

          // Reset camera error states before updating
          site.Cameras.forEach(c => {
            c.HasError = null;
            c.ErrorMessage = '';
          });

          // Update camera error states based on status
          cameras.forEach(camera => {
            const matchingCamera = site.Cameras.find(c => c.AcsId === camera.CameraId.Id);
            if (matchingCamera) {
              switch (camera.Status) {
                case 1: // Ok
                  matchingCamera.HasError = false;
                  matchingCamera.ErrorMessage = '';
                  break;
                case 2: // Unauthenticated
                  matchingCamera.HasError = true;
                  matchingCamera.ErrorMessage = 'Unable to authenticate to the camera.';
                  break;
                case 3: // NotAccessible
                  matchingCamera.HasError = true;
                  matchingCamera.ErrorMessage = 'Camera is not accessible.';
                  break;
              }
            }
            else {
              console.warn(`Camera [${camera.Name}] (${camera.CameraId.Id}) not found in site [${site.Name}] camera list.`);
            }
          });
        } catch (err) {
          // This likely a network error or the site is offline, so we mark the site and all its cameras as having an error
          console.error(`Failed to get camera status for site [${site.Name}]:`, err);
          site.HasError = true;
          site.ErrorMessage = `${site.Name} appears to be offline.`;

          // Mark all cameras as having an error since we couldn't retrieve their status
          site.Cameras.forEach(c => {
            c.HasError = true;
            c.ErrorMessage = 'Site appears to be offline, unable to retrieve camera status.';
          });
        }
      }
    };

    const workerCount = Math.min(maxConcurrentChecks, sites.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
  }

  /**
   * Get sites for a specific user
   * @param  {number} userId - User ID
   * @returns {Promise<SiteModel[]>} Promise
   */
  async GetSitesForUser(userId: number): Promise<SiteModel[]> {
    return new Promise((resolve, reject) => {
      this.http
        .get<SiteModel[]>(
          `${environment.managementApi}/Sites/User/${userId}/?includeCameras=true`
        )
        .subscribe({
          next: (sites) => resolve(sites),
          error: (err) => reject(err)
        }
        );
    });
  }

  /**
   * Delete a site by ID
   * @param  {number} siteId - Site ID
   * @returns {Promise<void>} Promise
   */
  async deleteSite(siteId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .delete(`${environment.managementApi}/sites/${siteId}/delete`)
        .subscribe({
          next: () => {
            this.sites = this.sites.filter((s) => s.Id != siteId);
            resolve();
          },
          error: (err) => reject(err)
        }
        );
    });
  }

  /**
   * Update an existing site
   * @param  {SiteModel} site - Site model to update
   * @returns {Promise<SiteModel>}
   */
  async updateSite(site: SiteModel): Promise<SiteModel> {
    return new Promise((resolve, reject) => {
      this.http
        .put<SiteModel>(`${environment.managementApi}/sites/update`, site)
        .subscribe({
          next: (rtnval) => {
            let index = this.sites.findIndex((s) => s.Id == rtnval.Id);
            this.sites[index] = rtnval;
            resolve(rtnval);
          },
          error: (err) => reject(err)
        }
        );
    });
  }

  /**
   * Create a new site
   * @param  {SiteModel} site - Site model to create
   * @returns {Promise<SiteModel>}
   */
  async createNewSite(site: SiteModel): Promise<SiteModel> {
    return new Promise((resolve, reject) => {
      this.http
        .post<SiteModel>(`${environment.managementApi}/sites/add`, site)
        .subscribe({
          next: (rtnval) => {
            this.sites.push(rtnval);
            resolve(rtnval);
          },
          error: (err) => reject(err)
        }
        );
    });
  }
}
